import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    TextField,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert
} from '@mui/material';
import {
    Save,
    Close,
    Delete,
    Add,
    ExpandMore
} from '@mui/icons-material';
import { SoWData, ComplianceRequirement, MaterialScheduleItem, ScopeOfWorksPhase, ScopeLineItem } from '../services/sowService';

interface EditableSoWProps {
    sowData: SoWData;
    onSave: (updatedSow: SoWData) => void;
    onCancel: () => void;
}

const EditableSoW: React.FC<EditableSoWProps> = ({ sowData, onSave, onCancel }) => {
    // Deep copy to avoid mutating props directly
    const [formData, setFormData] = useState<SoWData>(JSON.parse(JSON.stringify(sowData)));

    // --- PROJECT SUMMARY HANDLERS ---
    const handleProjectSummaryChange = (field: 'description' | 'site_access_constraints' | 'occupancy_status', value: string) => {
        setFormData({
            ...formData,
            tender_document: {
                ...formData.tender_document,
                project_summary: {
                    ...formData.tender_document.project_summary,
                    [field]: value
                }
            }
        });
    };

    // --- COMPLIANCE REQUIREMENT HANDLERS ---
    const handleComplianceChange = (index: number, field: keyof ComplianceRequirement, value: string) => {
        const newCompliance = [...formData.tender_document.compliance_requirements];
        newCompliance[index] = { ...newCompliance[index], [field]: value };
        setFormData({
            ...formData,
            tender_document: {
                ...formData.tender_document,
                compliance_requirements: newCompliance
            }
        });
    };

    const handleDeleteCompliance = (index: number) => {
        const newCompliance = [...formData.tender_document.compliance_requirements];
        newCompliance.splice(index, 1);
        setFormData({
            ...formData,
            tender_document: {
                ...formData.tender_document,
                compliance_requirements: newCompliance
            }
        });
    };

    const handleAddCompliance = () => {
        setFormData({
            ...formData,
            tender_document: {
                ...formData.tender_document,
                compliance_requirements: [
                    ...formData.tender_document.compliance_requirements,
                    { certificate: 'New Certificate', trigger: '' }
                ]
            }
        });
    };

    // --- MATERIAL SCHEDULE HANDLERS ---
    const handleMaterialChange = (index: number, field: keyof MaterialScheduleItem, value: string | number) => {
        const newMaterials = [...formData.tender_document.material_schedule];
        newMaterials[index] = { ...newMaterials[index], [field]: value };
        setFormData({
            ...formData,
            tender_document: {
                ...formData.tender_document,
                material_schedule: newMaterials
            }
        });
    };

    const handleDeleteMaterial = (index: number) => {
        const newMaterials = [...formData.tender_document.material_schedule];
        newMaterials.splice(index, 1);
        setFormData({
            ...formData,
            tender_document: {
                ...formData.tender_document,
                material_schedule: newMaterials
            }
        });
    };

    const handleAddMaterial = () => {
        setFormData({
            ...formData,
            tender_document: {
                ...formData.tender_document,
                material_schedule: [
                    ...formData.tender_document.material_schedule,
                    {
                        category: 'General',
                        item_name: 'New Material',
                        quantity: 1,
                        unit: 'number',
                        supply_responsibility: 'Builder',
                        notes: ''
                    }
                ]
            }
        });
    };

    // --- SCOPE OF WORKS HANDLERS ---
    const handlePhaseChange = (phaseIndex: number, field: 'phase_name', value: string) => {
        const newPhases = [...formData.tender_document.scope_of_works];
        newPhases[phaseIndex] = { ...newPhases[phaseIndex], [field]: value };
        setFormData({
            ...formData,
            tender_document: {
                ...formData.tender_document,
                scope_of_works: newPhases
            }
        });
    };

    const handleLineItemChange = (phaseIndex: number, itemIndex: number, field: keyof ScopeLineItem, value: string | number) => {
        const newPhases = [...formData.tender_document.scope_of_works];
        const newItems = [...newPhases[phaseIndex].line_items];
        newItems[itemIndex] = { ...newItems[itemIndex], [field]: value };
        newPhases[phaseIndex] = { ...newPhases[phaseIndex], line_items: newItems };
        setFormData({
            ...formData,
            tender_document: {
                ...formData.tender_document,
                scope_of_works: newPhases
            }
        });
    };

    const handleDeleteLineItem = (phaseIndex: number, itemIndex: number) => {
        const newPhases = [...formData.tender_document.scope_of_works];
        const newItems = [...newPhases[phaseIndex].line_items];
        newItems.splice(itemIndex, 1);
        newPhases[phaseIndex] = { ...newPhases[phaseIndex], line_items: newItems };
        setFormData({
            ...formData,
            tender_document: {
                ...formData.tender_document,
                scope_of_works: newPhases
            }
        });
    };

    const handleAddLineItem = (phaseIndex: number) => {
        const newPhases = [...formData.tender_document.scope_of_works];
        newPhases[phaseIndex] = {
            ...newPhases[phaseIndex],
            line_items: [
                ...newPhases[phaseIndex].line_items,
                {
                    description: 'New line item',
                    pricing_note: '',
                    quantifiable_unit: 'number',
                    quantity: 1
                }
            ]
        };
        setFormData({
            ...formData,
            tender_document: {
                ...formData.tender_document,
                scope_of_works: newPhases
            }
        });
    };

    const handleExclusionChange = (phaseIndex: number, exclusionIndex: number, value: string) => {
        const newPhases = [...formData.tender_document.scope_of_works];
        const newExclusions = [...newPhases[phaseIndex].exclusions];
        newExclusions[exclusionIndex] = value;
        newPhases[phaseIndex] = { ...newPhases[phaseIndex], exclusions: newExclusions };
        setFormData({
            ...formData,
            tender_document: {
                ...formData.tender_document,
                scope_of_works: newPhases
            }
        });
    };

    const handleDeleteExclusion = (phaseIndex: number, exclusionIndex: number) => {
        const newPhases = [...formData.tender_document.scope_of_works];
        const newExclusions = [...newPhases[phaseIndex].exclusions];
        newExclusions.splice(exclusionIndex, 1);
        newPhases[phaseIndex] = { ...newPhases[phaseIndex], exclusions: newExclusions };
        setFormData({
            ...formData,
            tender_document: {
                ...formData.tender_document,
                scope_of_works: newPhases
            }
        });
    };

    const handleAddExclusion = (phaseIndex: number) => {
        const newPhases = [...formData.tender_document.scope_of_works];
        newPhases[phaseIndex] = {
            ...newPhases[phaseIndex],
            exclusions: [...newPhases[phaseIndex].exclusions, 'New exclusion']
        };
        setFormData({
            ...formData,
            tender_document: {
                ...formData.tender_document,
                scope_of_works: newPhases
            }
        });
    };

    const handleDeletePhase = (phaseIndex: number) => {
        const newPhases = [...formData.tender_document.scope_of_works];
        newPhases.splice(phaseIndex, 1);
        setFormData({
            ...formData,
            tender_document: {
                ...formData.tender_document,
                scope_of_works: newPhases
            }
        });
    };

    const handleAddPhase = () => {
        setFormData({
            ...formData,
            tender_document: {
                ...formData.tender_document,
                scope_of_works: [
                    ...formData.tender_document.scope_of_works,
                    {
                        phase_name: 'New Phase',
                        line_items: [],
                        exclusions: []
                    }
                ]
            }
        });
    };

    const handleSave = () => {
        onSave(formData);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">Edit Statement of Work</Typography>
                <Box>
                    <Button variant="outlined" onClick={onCancel} sx={{ mr: 2 }} startIcon={<Close />}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSave} startIcon={<Save />}>
                        Save Changes
                    </Button>
                </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
                Make changes to your project specification. Note: Internal estimates are automatically calculated and cannot be edited manually.
            </Alert>

            {/* --- TITLE --- */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <TextField
                        label="Project Title"
                        fullWidth
                        value={formData.tender_document.title}
                        onChange={(e) => setFormData({
                            ...formData,
                            tender_document: { ...formData.tender_document, title: e.target.value }
                        })}
                        sx={{ mb: 2 }}
                    />
                </CardContent>
            </Card>

            {/* --- PROJECT SUMMARY --- */}
            <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">Project Summary</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Description"
                            fullWidth
                            multiline
                            rows={3}
                            value={formData.tender_document.project_summary.description}
                            onChange={(e) => handleProjectSummaryChange('description', e.target.value)}
                        />
                        <TextField
                            label="Site Access Constraints"
                            fullWidth
                            multiline
                            rows={2}
                            value={formData.tender_document.project_summary.site_access_constraints}
                            onChange={(e) => handleProjectSummaryChange('site_access_constraints', e.target.value)}
                        />
                        <TextField
                            label="Occupancy Status"
                            fullWidth
                            value={formData.tender_document.project_summary.occupancy_status}
                            onChange={(e) => handleProjectSummaryChange('occupancy_status', e.target.value)}
                        />
                    </Box>
                </AccordionDetails>
            </Accordion>

            {/* --- COMPLIANCE REQUIREMENTS --- */}
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">Compliance Requirements ({formData.tender_document.compliance_requirements.length})</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {formData.tender_document.compliance_requirements.map((req, index) => (
                        <Card key={index} sx={{ mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                    <TextField
                                        label="Certificate/Requirement"
                                        fullWidth
                                        value={req.certificate}
                                        onChange={(e) => handleComplianceChange(index, 'certificate', e.target.value)}
                                    />
                                    <IconButton color="error" onClick={() => handleDeleteCompliance(index)}>
                                        <Delete />
                                    </IconButton>
                                </Box>
                                <TextField
                                    label="Trigger/Details"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    value={req.trigger}
                                    onChange={(e) => handleComplianceChange(index, 'trigger', e.target.value)}
                                />
                            </CardContent>
                        </Card>
                    ))}
                    <Button startIcon={<Add />} onClick={handleAddCompliance}>
                        Add Compliance Requirement
                    </Button>
                </AccordionDetails>
            </Accordion>

            {/* --- MATERIAL SCHEDULE --- */}
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">Material Schedule ({formData.tender_document.material_schedule.length} items)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'grey.100' }}>
                                    <TableCell>Category</TableCell>
                                    <TableCell>Item Name</TableCell>
                                    <TableCell width="10%">Quantity</TableCell>
                                    <TableCell width="10%">Unit</TableCell>
                                    <TableCell width="12%">Supply By</TableCell>
                                    <TableCell width="25%">Notes</TableCell>
                                    <TableCell width="5%">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {formData.tender_document.material_schedule.map((material, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <TextField
                                                size="small"
                                                value={material.category}
                                                onChange={(e) => handleMaterialChange(index, 'category', e.target.value)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                value={material.item_name}
                                                onChange={(e) => handleMaterialChange(index, 'item_name', e.target.value)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                type="number"
                                                size="small"
                                                value={material.quantity}
                                                onChange={(e) => handleMaterialChange(index, 'quantity', Number(e.target.value))}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                size="small"
                                                value={material.unit}
                                                onChange={(e) => handleMaterialChange(index, 'unit', e.target.value)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <FormControl size="small" fullWidth>
                                                <Select
                                                    value={material.supply_responsibility}
                                                    onChange={(e) => handleMaterialChange(index, 'supply_responsibility', e.target.value)}
                                                >
                                                    <MenuItem value="Client">Client</MenuItem>
                                                    <MenuItem value="Builder">Builder</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                multiline
                                                maxRows={2}
                                                value={material.notes}
                                                onChange={(e) => handleMaterialChange(index, 'notes', e.target.value)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <IconButton size="small" color="error" onClick={() => handleDeleteMaterial(index)}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Button startIcon={<Add />} onClick={handleAddMaterial} sx={{ mt: 2 }}>
                        Add Material
                    </Button>
                </AccordionDetails>
            </Accordion>

            {/* --- SCOPE OF WORKS --- */}
            <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">Scope of Works ({formData.tender_document.scope_of_works.length} phases)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {formData.tender_document.scope_of_works.map((phase, phaseIndex) => (
                            <Card key={phaseIndex} sx={{ border: '2px solid #e0e0e0' }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                                        <TextField
                                            label="Phase Name"
                                            fullWidth
                                            value={phase.phase_name}
                                            onChange={(e) => handlePhaseChange(phaseIndex, 'phase_name', e.target.value)}
                                        />
                                        <IconButton color="error" onClick={() => handleDeletePhase(phaseIndex)}>
                                            <Delete />
                                        </IconButton>
                                    </Box>

                                    {/* Line Items */}
                                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
                                        Line Items ({phase.line_items.length})
                                    </Typography>
                                    <TableContainer component={Paper} sx={{ mb: 2 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: 'grey.50' }}>
                                                    <TableCell width="40%">Description</TableCell>
                                                    <TableCell width="30%">Pricing Note</TableCell>
                                                    <TableCell width="10%">Quantity</TableCell>
                                                    <TableCell width="15%">Unit</TableCell>
                                                    <TableCell width="5%">Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {phase.line_items.map((item, itemIndex) => (
                                                    <TableRow key={itemIndex}>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                fullWidth
                                                                multiline
                                                                maxRows={3}
                                                                value={item.description}
                                                                onChange={(e) => handleLineItemChange(phaseIndex, itemIndex, 'description', e.target.value)}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                fullWidth
                                                                multiline
                                                                maxRows={2}
                                                                value={item.pricing_note}
                                                                onChange={(e) => handleLineItemChange(phaseIndex, itemIndex, 'pricing_note', e.target.value)}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                type="number"
                                                                size="small"
                                                                value={item.quantity}
                                                                onChange={(e) => handleLineItemChange(phaseIndex, itemIndex, 'quantity', Number(e.target.value))}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={item.quantifiable_unit}
                                                                onChange={(e) => handleLineItemChange(phaseIndex, itemIndex, 'quantifiable_unit', e.target.value)}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <IconButton size="small" color="error" onClick={() => handleDeleteLineItem(phaseIndex, itemIndex)}>
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <Button size="small" startIcon={<Add />} onClick={() => handleAddLineItem(phaseIndex)}>
                                        Add Line Item
                                    </Button>

                                    <Divider sx={{ my: 2 }} />

                                    {/* Exclusions */}
                                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'error.main' }}>
                                        Exclusions ({phase.exclusions.length})
                                    </Typography>
                                    {phase.exclusions.map((exclusion, excIndex) => (
                                        <Box key={excIndex} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                value={exclusion}
                                                onChange={(e) => handleExclusionChange(phaseIndex, excIndex, e.target.value)}
                                            />
                                            <IconButton size="small" color="error" onClick={() => handleDeleteExclusion(phaseIndex, excIndex)}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                    <Button size="small" startIcon={<Add />} onClick={() => handleAddExclusion(phaseIndex)}>
                                        Add Exclusion
                                    </Button>
                                </CardContent>
                            </Card>
                        ))}
                        <Button startIcon={<Add />} onClick={handleAddPhase} variant="outlined">
                            Add New Phase
                        </Button>
                    </Box>
                </AccordionDetails>
            </Accordion>

            {/* Save/Cancel Buttons at Bottom */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4, mb: 2 }}>
                <Button variant="outlined" onClick={onCancel} startIcon={<Close />}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSave} startIcon={<Save />}>
                    Save Changes
                </Button>
            </Box>
        </Box>
    );
};

export default EditableSoW;